import axios from "axios";

// Production is same-origin through CloudFront. Local development may point
// this at the backend directly.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

const SAFE_METHODS = new Set(["get", "head", "options"]);

export function cookieValue(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

api.interceptors.request.use((config) => {
  // The final session lives only in the HttpOnly cookie. Bearer headers remain
  // reserved for short-lived MFA/enrollment tokens supplied by those flows.
  const method = (config.method || "get").toLowerCase();
  if (!SAFE_METHODS.has(method) && !config.headers?.Authorization) {
    const csrf = cookieValue("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * FR-27: records are voided with a mandatory reason, never deleted.
 * Prompts for the reason and performs the void; returns false if cancelled.
 *
 * `copy` overrides the default "hides the record" explanation for callers
 * where that is not accurate - e.g. withdrawing a message does not hide
 * anything: it stays in place and the body is still served, only its
 * rendering changes.
 */
export async function voidRecord(url, label, copy = "This hides the record but keeps it retrievable.") {
  const reason = window.prompt(`Void ${label}. ${copy}\nReason (required):`);
  if (reason === null) return false;
  if (!reason.trim()) {
    window.alert("A reason is required to void a record.");
    return false;
  }
  await api.delete(url, { data: { reason: reason.trim() } });
  return true;
}

export default api;
